import React from 'react';
import { Plus, Layers, BookOpenCheck, Send, FileText, Activity, Settings } from 'lucide-react';

/**
 * CpscCreateManageNav — CHA-267 PR-1 (nav + routing, BEHAVIOR-FROZEN).
 *
 * The 9-tab CPSC strip becomes ONE nav with two groups:
 *   CREATE — a single "+ New Certificate / Exemption" entry (the four input methods live on the
 *            Create landing, CpscCreateLanding.jsx);
 *   MANAGE — Drafts · Register · Submissions · Broker Handoff · Audit & History · Settings (PR-3
 *            completed the set: Register = the 3-way merged list, Ruling 4).
 *
 * This file also owns the CPSC sub-route vocabulary (CHA-267 Ruling 7): views are addressable as
 * `#/imports/cpsc/<view>`, and the OLD tab ids map to their new homes so pre-CHA-267 muscle memory
 * (and any written-down paths) still lands somewhere sensible:
 *
 *   bv-upload → create/bv · manual-coc → create/manual · exemptions → create/decode ·
 *   cert-links → create/link · status-checker → submissions · audit-history → audit ·
 *   drafts/broker-handoff/settings → unchanged ids.
 *
 * Styling uses the CHA-239 Carbon tokens (carbon-blue-60 active, carbon-ink/carbon-gray-* text) —
 * the pre-reskin slate/orange CpscTabBar is deliberately NOT reused here (CHA-239 alignment); it
 * still serves the untouched Imports + Settings strips.
 *
 * ZERO behavior change: this component only selects which (unchanged) tab component renders.
 */

// The addressable CPSC views (sub-route segment after `imports/cpsc/`). Create's method views are
// two-segment so a method is itself linkable (`create/decode` etc.).
export const CPSC_VIEWS = [
  'create', 'create/decode', 'create/bv', 'create/manual', 'create/link',
  'drafts', 'register', 'submissions', 'broker-handoff', 'audit', 'settings',
];

// Old tab id → new view (Ruling 7: old tab IDs map to new views).
export const LEGACY_CPSC_VIEW = {
  'bv-upload': 'create/bv',
  'manual-coc': 'create/manual',
  'exemptions': 'create/decode',
  'cert-links': 'create/link',
  'status-checker': 'submissions',
  'audit-history': 'audit',
  drafts: 'drafts',
  'broker-handoff': 'broker-handoff',
  settings: 'settings',
};

// PURE — normalize any incoming sub-route segment (new view, legacy tab id, or junk) to a valid
// view, or null when it isn't one (callers fall back to the default view; never crash on a bad URL).
export function normalizeCpscView(segment) {
  const s = String(segment == null ? '' : segment).replace(/^\/+|\/+$/g, '');
  if (!s) return null;
  if (CPSC_VIEWS.includes(s)) return s;
  if (LEGACY_CPSC_VIEW[s]) return LEGACY_CPSC_VIEW[s];
  return null;
}

const MANAGE_ITEMS = [
  { id: 'drafts', label: 'Drafts', icon: Layers },
  { id: 'register', label: 'Register', icon: BookOpenCheck }, // CHA-267 PR-3 — the 3-way merged list
  { id: 'submissions', label: 'Submissions', icon: Send },
  { id: 'broker-handoff', label: 'Broker Handoff', icon: FileText },
  { id: 'audit', label: 'Audit & History', icon: Activity },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export default function CpscCreateManageNav({ activeView, onSelect }) {
  // CHA-278 — Carbon pass (CPSC concept rail, laid out as the horizontal strip the shipped IA uses):
  // CREATE = the one solid accent entry; MANAGE = mono uppercase eyebrow + items whose active state is the
  // Carbon 3px accent underline on gray-10. Presentation only — same items, same onSelect wiring.
  const inCreate = activeView === 'create' || String(activeView || '').startsWith('create/');
  return (
    <div className="font-plex flex items-center gap-1 border-b border-carbon-border bg-carbon-white px-3 py-2 flex-wrap" aria-label="cpsc navigation">
      {/* CREATE — the one entry; the landing offers the four methods */}
      <button
        onClick={() => onSelect('create')}
        aria-label="cpsc create"
        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold transition-colors ${
          inCreate ? 'bg-carbon-blue-60 text-white hover:bg-carbon-blue-70' : 'bg-carbon-gray-10 text-carbon-ink hover:bg-carbon-gray-20'
        }`}
      >
        <Plus className="h-4 w-4" /> New Certificate / Exemption
      </button>

      <span className="mx-2 h-5 w-px bg-carbon-border" aria-hidden="true" />
      <span className="font-plex-mono text-[10px] uppercase tracking-[0.14em] text-carbon-gray-50 mr-1">Manage</span>

      {MANAGE_ITEMS.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          onClick={() => onSelect(id)}
          aria-label={`cpsc manage ${label.toLowerCase()}`}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm border-b-2 -mb-0.5 transition-colors ${
            activeView === id
              ? 'text-carbon-ink font-semibold bg-carbon-gray-10 border-carbon-blue-60'
              : 'text-carbon-gray-70 font-medium border-transparent hover:text-carbon-ink hover:bg-carbon-gray-10'
          }`}
        >
          <Icon className="h-4 w-4" /> {label}
        </button>
      ))}
    </div>
  );
}
